import { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { message as antdMessage } from "antd";

import { apiVerifyStampCode, apiScanSelectTelecom, apiScanSetTopupPhone } from "@/api";
import { FormVerificationData } from "@/types";
import { reverseGeocode } from "@/utils";

import { Card } from "antd";
import { isEmpty } from "@/utils";
import { useScanQrcodeStore } from "@/store";
import { STAMP_CODE_VERIFIED, STATUS_VERIFY, STAMP_STATUS, VERIFICATION_TYPE } from "@/constants";

import CommonSlider from "@/components/common/CommonSlider";
import VerifyInfo from "@/components/elements/VerifyInfo";
import ProductDetail from "@/components/common/ProductDetail";
import ProductSwiper from "@/components/common/ProductSwiper";
import CommonContact from "@/components/common/CommonContact";
import CommonFooter from "@/components/common/CommonFooter";
import StampStatusVerification from "@/components/elements/StampStatusVerification";
import FormVerification from "@/components/elements/FormVerification";
import CustomerProfile from "@/components/common/CustomerProfile";
import ScratchCardRewardCard from "@/components/templates/verifies/ScratchCardRewardCard";

const TemplateOne: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    params,
    stamp_code,
    product,
    isSerial,
    scratch_card_reward,
    setDataScanQrcode,
    setStatusScanStampCode,
    setVerificationLocation,
  } = useScanQrcodeStore();
  const { status, customer } = stamp_code || {};

  const [selectingTelecom, setSelectingTelecom] = useState(false);
  // Vừa nhận thưởng TRONG phiên này (xác thực / chọn nhà mạng / nhập SĐT) -> chúc mừng.
  // Mở lại trang (cờ = false) thì luồng nạp tự động chỉ hiện trạng thái trả thưởng.
  // KHÔNG bó theo status === "issued": nạp tự động bất đồng bộ nên vừa kích hoạt xong
  // reward mới ở "processing", vẫn phải chúc mừng ngay.
  const [justRewarded, setJustRewarded] = useState(false);

  const _STAMP_CODE_VERIFIED = useMemo(
    () => [...STAMP_CODE_VERIFIED, STATUS_VERIFY.OVER_LIMITED, STAMP_STATUS.WARRANTY_REPLACED],
    [],
  );

  const shouldShowVerifyInfo = useMemo(() => {
    return !isEmpty(customer) && _STAMP_CODE_VERIFIED.includes(status as string);
  }, [customer, status, _STAMP_CODE_VERIFIED]);

  // Show customer fields condition
  const stampHasWarranty = stamp_code?.has_warranty || false;
  const showCustomerStampAntiCounterfeit = stamp_code?.is_show_customer_stamp_anti_counterfeit || false;
  const shouldShowCustomerFields = useMemo(() => {
    return showCustomerStampAntiCounterfeit || stampHasWarranty || !isSerial;
  }, [showCustomerStampAntiCounterfeit, stampHasWarranty, isSerial]);

  const hasProduct = useMemo(() => {
    return !isEmpty(product);
  }, [product]);

  const hasRelatedProducts = useMemo(() => {
    return product?.related_products?.length > 0;
  }, [product]);

  const preview = params?.preview || false;
  const xid = params?.xid || "";
  const user_uuid = params?.user_uuid || "";
  const lat = params?.lat || "0";
  const lon = params?.lon || "0";
  const utm = params?.utm || "";
  const scanType = VERIFICATION_TYPE.LANDING_PAGE;
  const browser_id = window.localStorage.getItem("browser_id");
  const serialx = params?.serial || params?.ser;

  const handleSubmit = async (values: FormVerificationData) => {
    try {
      const data = {
        type: scanType,
        xid,
        serial: serialx,
        ...values,
        browser_id,
        user_uuid,
        lat,
        lon,
        utm,
      };

      const response = await apiVerifyStampCode(data);
      const { data: dataResponse } = response;
      setDataScanQrcode(dataResponse);
      if (dataResponse?.scratch_card_reward) setJustRewarded(true);

      // Bổ sung full_address nếu verification_location có lat/lon nhưng thiếu full_address
      const verificationLoc = dataResponse?.verification_location;
      if (verificationLoc && !verificationLoc.full_address && verificationLoc.latitude && verificationLoc.longitude) {
        // Lấy địa chỉ trong background (non-blocking)
        reverseGeocode(verificationLoc.latitude, verificationLoc.longitude).then((address) => {
          if (address) {
            const updatedLocation = { ...verificationLoc, full_address: address };
            setVerificationLocation(updatedLocation);
          }
        });
      }
    } catch (e) {
      console.log("[QRX] error handle event submit", e);
      if (!serialx) {
        await navigate("/error");
        return;
      }
      setStatusScanStampCode("fail");
    }
  };

  // C14 — khách chọn nhà mạng nhận thẻ cào (phát thẻ theo nhà mạng đã chọn).
  const handleSelectTelecom = async (network: number) => {
    if (preview) {
      antdMessage.warning(t("scratch_card.preview_blocked"));
      return;
    }
    setSelectingTelecom(true);
    try {
      const response = await apiScanSelectTelecom({ xid, serial: serialx, network });
      const data = response?.data;
      if (data && data.stamp_code?.status !== "not_found") {
        setDataScanQrcode(data);
        if (data.scratch_card_reward) setJustRewarded(true);
      }
      const reward = data?.scratch_card_reward;
      if (reward && reward.status !== "issued" && reward.error) {
        antdMessage.error(reward.error);
      }
    } catch (e) {
      console.log("[QRX] error select telecom", (e as Error)?.message);
      antdMessage.error(t("scratch_card.select_error"));
    } finally {
      setSelectingTelecom(false);
    }
  };

  // D6 — khách nhập SĐT để nhận nạp tự động (topup).
  const handleSetTopupPhone = async (phone: string) => {
    if (preview) {
      antdMessage.warning(t("scratch_card.preview_blocked"));
      return;
    }
    setSelectingTelecom(true);
    try {
      const response = await apiScanSetTopupPhone({ xid, serial: serialx, phone });
      const data = response?.data;
      if (data && data.stamp_code?.status !== "not_found") {
        setDataScanQrcode(data);
        if (data.scratch_card_reward) setJustRewarded(true);
      }
      const reward = data?.scratch_card_reward;
      if (reward && reward.status !== "issued" && reward.status !== "processing" && reward.error) {
        antdMessage.error(reward.error);
      }
    } catch (e) {
      console.log("[QRX] error set topup phone", (e as Error)?.message);
      antdMessage.error(t("scratch_card.topup_error"));
    } finally {
      setSelectingTelecom(false);
    }
  };

  return (
    <div className="qrx-template-one m-auto min-h-screen">
      <Card className="qrx-card-bank mb-3">
        {(isSerial || !isEmpty(product)) && <CommonSlider images={product?.images} />}

        <StampStatusVerification />

        {(!status || status === STAMP_STATUS.SOLD) && (
          <>
            <div className="qrx-bg--success qrx-verify-strip p-4 text-white">
              {t("common.verification_product")}
            </div>
            <div className="p-5">
              <h2 className="text-xl font-bold text-gray-800 mb-2">{product?.name || ""}</h2>
              {stamp_code.landing_page_note_content.length ? (
                <div className="text-xs mb-3">{stamp_code.landing_page_note_content}</div>
              ) : (
                <div className="text-xs mb-3" dangerouslySetInnerHTML={{ __html: t("common.note_verification") }} />
              )}

              {stamp_code.is_show_verification_form && (
                <FormVerification
                  preview={preview}
                  shouldShowCustomerFields={shouldShowCustomerFields}
                  onSubmit={handleSubmit}
                />
              )}
            </div>
          </>
        )}
      </Card>

      {scratch_card_reward && (
        <div className="!my-3">
          <ScratchCardRewardCard
            reward={scratch_card_reward}
            selecting={selectingTelecom}
            preview={preview}
            justRewarded={justRewarded}
            onSelectTelecom={handleSelectTelecom}
            onSetPhone={handleSetTopupPhone}
          />
        </div>
      )}

      {shouldShowVerifyInfo && <VerifyInfo className="!my-3" />}

      <CustomerProfile className="!my-3" />

      {hasProduct && <ProductDetail className="!my-3" />}

      {hasRelatedProducts && <ProductSwiper className="!my-3" />}

      <CommonContact />
      <CommonFooter />
    </div>
  );
};

export default TemplateOne;
